import React, { useState, useEffect } from 'react';
import { 
  RefreshCw, 
  Info,
  Edit3,
  X,
  Check,
  Calendar
} from 'lucide-react';
import scenarioApi from '../../api/scenarioApi';
import { theme } from '../../styles/theme';

interface DefaultTemporalDistributionsProps {
  darkMode: boolean;
  isAdmin: boolean;
}

interface YearlyPercentage {
  year: number;
  percentage: number;
}

const DefaultTemporalDistributions: React.FC<DefaultTemporalDistributionsProps> = ({ 
  darkMode, 
  isAdmin 
}) => {
  const [distribution, setDistribution] = useState<YearlyPercentage[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editedDistribution, setEditedDistribution] = useState<YearlyPercentage[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Fetch default distribution on mount
  useEffect(() => {
    fetchDefaultDistribution();
  }, []);

  const fetchDefaultDistribution = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await scenarioApi.getDefaultTemporalDistribution();
      const dist = response.yearly_percentages || generateDefaultDistribution();
      setDistribution(dist);
    } catch (err) {
      console.error('Failed to fetch default distribution:', err);
      // If no default exists, use a generated one
      const defaultDist = generateDefaultDistribution();
      setDistribution(defaultDist);
    } finally {
      setLoading(false);
    }
  };

  const generateDefaultDistribution = (): YearlyPercentage[] => {
    // Generate a balanced 6-year distribution
    return [
      { year: 1, percentage: 15 },
      { year: 2, percentage: 20 },
      { year: 3, percentage: 20 },
      { year: 4, percentage: 20 },
      { year: 5, percentage: 15 },
      { year: 6, percentage: 10 }
    ];
  };

  const handleEdit = () => {
    setEditedDistribution([...distribution]);
    setEditing(true);
  };

  const handleCancel = () => {
    setEditing(false);
    setEditedDistribution([]);
    setError(null);
  };

  const handlePercentageChange = (yearIndex: number, value: string) => {
    const numValue = parseFloat(value) || 0;
    const newDist = [...editedDistribution];
    newDist[yearIndex] = { ...newDist[yearIndex], percentage: numValue };
    setEditedDistribution(newDist);
  };

  const validateDistribution = (dist: YearlyPercentage[]): string | null => {
    const total = dist.reduce((sum, item) => sum + item.percentage, 0);
    if (Math.abs(total - 100) > 0.01) {
      return `Percentages must sum to 100% (currently ${total.toFixed(1)}%)`;
    }
    if (dist.some(item => item.percentage < 0)) {
      return 'Percentages cannot be negative';
    }
    if (dist.some(item => item.percentage > 100)) {
      return 'Individual percentages cannot exceed 100%';
    }
    return null;
  };

  const handleSave = async () => {
    const validationError = validateDistribution(editedDistribution);
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await scenarioApi.updateDefaultTemporalDistribution({
        yearly_percentages: editedDistribution,
        temporal_type: 'custom'
      });

      // Update local state
      setDistribution(editedDistribution);
      setEditing(false);
      setEditedDistribution([]);
      setSuccessMessage('Default temporal distribution updated successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Failed to save distribution:', err);
      setError('Failed to save default temporal distribution. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const normalizeDistribution = () => {
    const total = editedDistribution.reduce((sum, item) => sum + item.percentage, 0);
    if (total === 0) return;

    const normalized = editedDistribution.map(item => ({
      ...item,
      percentage: (item.percentage / total) * 100
    }));

    setEditedDistribution(normalized);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-4">
        <RefreshCw className="w-5 h-5 animate-spin" style={{ color: theme.colors.primary.main }} />
      </div>
    );
  }

  const currentDist = editing ? editedDistribution : distribution;
  const total = currentDist.reduce((sum, item) => sum + item.percentage, 0);

  return (
    <div className="px-4 py-3 rounded-lg" style={{
      backgroundColor: darkMode ? theme.colors.background.dark : theme.colors.background.default,
      border: `1px solid ${darkMode ? theme.colors.border.dark : theme.colors.border.light}`
    }}>
      {/* Header Row */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4" style={{ color: theme.colors.primary.main }} />
          <span className="text-sm font-medium" style={{
            color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary
          }}>
            Temporal Pattern (Years)
          </span>
        </div>
        {isAdmin && (
          <div className="flex gap-1">
            {editing ? (
              <>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="p-1 rounded transition-colors"
                  style={{
                    backgroundColor: '#10b98120',
                    color: '#10b981'
                  }}
                >
                  <Check className="w-3 h-3" />
                </button>
                <button
                  onClick={handleCancel}
                  disabled={saving}
                  className="p-1 rounded transition-colors"
                  style={{
                    backgroundColor: '#ef444420',
                    color: '#ef4444'
                  }}
                >
                  <X className="w-3 h-3" />
                </button>
              </>
            ) : (
              <button
                onClick={handleEdit}
                className="p-1 rounded transition-colors"
                style={{
                  backgroundColor: darkMode ? '#1a1a1a' : '#f9f9f9',
                  color: theme.colors.primary.main
                }}
              >
                <Edit3 className="w-3 h-3" />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="mb-2 p-2 rounded text-xs" style={{
          backgroundColor: '#ef444420',
          color: '#ef4444',
          border: '1px solid #ef444440'
        }}>
          {error}
        </div>
      )}
      {successMessage && (
        <div className="mb-2 p-2 rounded text-xs" style={{
          backgroundColor: '#10b98120',
          color: '#10b981',
          border: '1px solid #10b98140'
        }}>
          {successMessage}
        </div>
      )}

      {/* Distribution Values in a Single Row */}
      <div className="flex items-center gap-4">
        <div className="flex-1 grid grid-cols-6 gap-2">
          {currentDist.map((item, index) => (
            <div key={index} className="text-center">
              <div className="text-xs" style={{
                color: theme.colors.text.secondary
              }}>
                Year {item.year}
              </div>
              {editing ? (
                <input
                  type="number"
                  value={item.percentage}
                  onChange={(e) => handlePercentageChange(index, e.target.value)}
                  className="w-full px-1 py-0.5 rounded text-center text-sm font-medium mt-1"
                  style={{
                    backgroundColor: darkMode 
                      ? '#1a1a1a' 
                      : '#f9f9f9',
                    color: darkMode 
                      ? theme.colors.text.darkPrimary 
                      : theme.colors.text.primary,
                    border: `1px solid ${darkMode 
                      ? theme.colors.border.dark 
                      : theme.colors.border.light}`,
                    fontSize: '13px'
                  }}
                  step="0.1"
                  min="0"
                  max="100"
                />
              ) : (
                <div className="font-medium text-sm mt-0.5" style={{
                  color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary
                }}>
                  {item.percentage.toFixed(1)}%
                </div>
              )}
            </div>
          ))}
        </div>
        
        {/* Total */}
        <div className="flex items-center gap-2 pl-3" style={{
          borderLeft: `1px solid ${darkMode ? theme.colors.border.dark : theme.colors.border.light}`
        }}>
          <span className="text-xs" style={{
            color: theme.colors.text.secondary
          }}>
            Total:
          </span>
          <span className={`font-bold text-sm ${
            Math.abs(total - 100) < 0.01 ? 'text-green-500' : 'text-red-500'
          }`}>
            {total.toFixed(1)}%
          </span>
          {editing && Math.abs(total - 100) > 0.01 && (
            <button
              onClick={normalizeDistribution}
              className="text-xs px-2 py-0.5 rounded transition-colors ml-1"
              style={{
                backgroundColor: '#6366f120',
                color: theme.colors.primary.main
              }}
            >
              Normalize
            </button>
          )}
        </div>
      </div>

      {/* Compact Info Text */}
      {!editing && (
        <div className="mt-2 flex items-center gap-1">
          <Info className="w-3 h-3 flex-shrink-0" style={{
            color: theme.colors.text.secondary
          }} />
          <p className="text-xs" style={{
            color: theme.colors.text.secondary
          }}>
            Applied to all scenarios when no custom configuration exists
          </p>
        </div>
      )}
    </div>
  );
};

export default DefaultTemporalDistributions;